import { useEffect, useState } from 'react';

/**
 * useActiveSection — scroll-spy via IntersectionObserver.
 *
 * Returns the id of the section currently centered in viewport (or null when
 * none of the watched ids are visible). rootMargin pushes the activation zone
 * toward the upper third of the viewport so the active link matches what the
 * reader is actually looking at, not what's just barely entering at the bottom.
 *
 * Phase 27.28 — used by Navbar to highlight the active nav link on home.
 * On sub-pages (where these sections don't exist) the hook returns null and
 * no link is highlighted — desired behavior.
 */
export function useActiveSection(ids: readonly string[]): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    // Phase 27.72D — bail si aucun element. State reste à sa valeur précédente
    // (default null au mount). Ancien `setActive(null)` ici causait set-state-in-effect ;
    // edge case (ids change vers liste sans DOM elements) trop rare pour justifier reset.
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the most-intersecting visible section.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActive(visible[0].target.id);
        }
      },
      {
        // Activation band: upper third of viewport.
        rootMargin: '-25% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);

  return active;
}
